import {
  cutOutline,
  flag,
  home,
  homeOutline,
  pushOutline,
} from 'ionicons/icons';
import { assign } from 'lodash';
import { Scratchpad } from '../../../../../api/graphql/generated';
import { IntercomArticle } from '../../../../../config/intercom';
import i18n from '../../../../../i18n';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { QNear, Waypoint } from '../../../../../util/machine-variable.utils';
import { moveItem } from '../../../shared/component/icon/move-item';
import { reportProduction } from '../../../shared/component/icon/report-production';
import { waitForOperator } from '../../../shared/component/icon/wait-for-operator';
import {
  ApplicationDefaultParameterSetTagData,
  Flow,
  FlowItemMetadata,
  FlowItemParameterMetadata,
  FlowRule,
  PickerWidget,
  RuleValidationResponse,
  TeachIOMapping,
  Tool,
  ToolConfig,
  WaypointParameterValue,
  WidgetType,
} from '../../../shared/model/flow.types';
import { URModel } from '../../../shared/model/ur.types';
import { flowRuleCheckContiguousByType } from '../../../shared/util/flow-rule.util';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace BeaconCuttingData {
  export const APP_NAME = 'Beacon Cutting';
  export const ANALYTICS_PREFIX = 'Beacon Cutting';
  export const APP_SLUG = 'beacon_cutting';

  export enum MachinePreference {
    PART_CONFIG_DEFAULTS = 'beacon_cutting.part_config.defaults',
  }

  export enum Cutter {
    HYPERTHERM_POWERMAX_SYNC = 'hypertherm_powermax_sync',
    HYPERTHERM_POWERMAX_125 = 'hypertherm_powermax_125',
    THERMAL_DYNAMICS_CUTMASTER = 'thermal_dynamics_cutmaster',
  }

  export enum Gas {
    AIR = 'air',
    F5 = 'F5',
  }

  export enum Current {
    A_125 = 125,
    A_120 = 120,
    A_105 = 105,
    A_100 = 100,
    A_85 = 85,
    A_80 = 80,
    A_65 = 65,
    A_60 = 60,
    A_45 = 45,
    A_40 = 40,
    A_35 = 35,
    A_30 = 30,
  }

  export enum KerfDirection {
    LEFT = 'left',
    RIGHT = 'right',
  }

  export interface FlowConfig {
    home: {
      waypoint: Waypoint;
      qnear: QNear;
    };
    ioMapping: TeachIOMapping;
    torch: ToolConfig;
    cutting: {
      model: string;
      ioMap: {
        mode: string;
        trigger: string;
      };
    };
    current: number;
    gas: string;
    materialType?: string;
    materialThickness: number;
    betweenSpeed: number;
    pierceHeightHoldTime?: number;
    startAtId?: string;
    stepModeEnabled?: boolean;
  }

  export interface CuttingScratchpad extends Scratchpad {
    appData: Flow<FlowConfig>;
  }

  export interface License {
    cutter: string;
  }

  export enum CutTypeEnum {
    LINEAR = 1,
    RADIAL = 2,
    ROTARY = 5,
  }

  export enum CutProcessValue {
    CUT = 'cut',
    GOUGE = 'gouge',
  }

  export enum FlowItemRef {
    CUT = 'cut',
    END = 'end',
    MOVE = 'move',
    MOVE_HOME = 'move to home',
    PRODUCTION_REPORTING = 'report production',
    SECTION = 'section',
    START = 'start',
    TRIGGER_OUTPUT = 'trigger output',
    WAIT_FOR = 'wait for operator',
  }

  export enum FlowItemParameterRef {
    APPROACH = 'approach',
    CUT_HEIGHT = 'cutHeight',
    CUT_TYPE = 'cutType',
    DEPART = 'depart',
    EDGE_START = 'edgeStart',
    KERF_WIDTH = 'kerfWidth',
    KERF_DIRECTION = 'kerfDirection',
    MOVE_MOTION_TYPE = 'moveMotionType',
    MOVE_SPEED_ADJUSTMENT_RATIO = 'moveSpeedAdjustmentRatio',
    MOVE_TRAVEL_SPEED = 'moveTravelSpeed',
    CUT_BLEND_RADIUS = 'cutBlendRadius',
    MOVE_BLEND_RADIUS = 'moveBlendRadius',
    PIERCE_DELAY = 'pierceDelay',
    PIERCE_HEIGHT = 'pierceHeight',
    POINT = 'point',
    PROCESS = 'process',
    PRODUCTION_QUANTITY = 'productionQuanity',
    QNEAR = 'qnear',
    SECTION_COLOR = 'sectionColor',
    TORCH_HEIGHT_CONTROL_ENABLED = 'torchHeightControlEnabled',
    TOUCH_SURFACE_ENABLED = 'touchSurfaceEnabled',
    TRAVEL_SPEED = 'travelSpeed',
    TRIGGER_OUTPUT_PORT = 'triggerOutputPort',
    TRIGGER_OUTPUT_STATE = 'triggerOutputState',
    VOLTAGE = 'voltage',
    WAIT_FOR_OPERATOR_MESSAGE = 'operatorMessage',
    WAIT_FOR_INPUT = 'waitForInput',
    WAIT_FOR_TIME = 'waitForTime',
    WAIT_FOR_INPUT_STATE = 'waitForInputState',
    WAIT_FOR_TYPE_OF_INPUT = 'typeOfInput',
    WAYPOINT = 'waypoint',
  }

  export enum Material {
    STEEL = 'steel',
    STAINLESS = 'stainless_steel',
    ALUMINUM = 'aluminum',
  }

  /**
   * Units = millimeters
   * These were pulled from welding and cutting charts.
   */
  export enum MaterialThickness {
    // Gauge labels, sorted by mm value
    '26GA' = 0.5,
    '24GA' = 0.6,
    '22GA' = 0.8,
    '20GA' = 0.9,
    '18GA' = 1.2,
    '16GA' = 1.6,
    '14GA' = 2.0,
    '12GA' = 2.8,
    '10GA' = 3.6,
    // Inch labels, sorted by mm value
    '3/32' = 2.4,
    '1/8' = 3.2,
    '3/16' = 4.8,
    '1/4' = 6.4,
    '5/16' = 7.9,
    '3/8' = 9.5,
    '1/2' = 12.7,
    '5/8' = 15.9,
    '3/4' = 19.1,
    '7/8' = 22.2,
    '1_0' = 25.4,
    '1_1/8' = 28.6,
    '1_1/4' = 31.8,
    '1_3/8' = 34.9,
    '1_1/2' = 38.1,
    '1_5/8' = 41.3,
  }

  export enum FlowItemParameterTag {
    APPROACH = 'approach',
    PROCESS_CUT = 'cut',
    DEPART = 'depart',
    PROCESS_GOUGE = 'gouge',
    ROTARY = 'rotary',
    WAIT_FOR_INPUT = 'waitForInput',
    WAIT_FOR_TIME = 'waitForTime',
    WAIT_FOR_OPERATOR = 'waitForOperator',
  }

  export enum ApplicationDefaultParameterSetTag {
    CURRENT = 'current',
    CUTTER = 'cutter',
    GAS = 'gas',
    MATERIAL_TYPE = 'materialType',
    MATERIAL_THICKNESS = 'materialThickness',
    PROCESS = 'process',
    TORCH_TYPE = 'torchType',
  }

  export const ApplicationDefaultParameterSetTagToConfig: Partial<
    Record<ApplicationDefaultParameterSetTag, keyof FlowConfig>
  > = {
    current: 'current',
    gas: 'gas',
    materialThickness: 'materialThickness',
    materialType: 'materialType',
  };

  export const ProcessMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.PROCESS,
      apiName: 'process',
      name: 'cutting:cutting_process',
      tags: [FlowItemParameterTag.APPROACH],
      displayOrder: 2,
      ui: {
        widget: WidgetType.PICKER,
        widgetOptions: [
          {
            label: 'cutting:cut',
            value: CutProcessValue.CUT,
          },
          {
            label: 'cutting:gouge',
            value: CutProcessValue.GOUGE,
          },
        ],
      },
      value: {
        type: 'string',
        default: CutProcessValue.CUT,
      },
    };

  export const VoltageMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.VOLTAGE,
      apiName: 'voltage',
      name: 'cutting:arc_voltage',
      displayOrder: 5,
      tags: [
        FlowItemParameterTag.PROCESS_CUT,
        FlowItemParameterTag.PROCESS_GOUGE,
      ],
      ui: {
        widget: WidgetType.NUMBER,
        widgetOptions: {
          step: 0.1,
        },
      },
      value: {
        type: 'number',
        minimum: 50,
        maximum: 200,
        required: true,
      },
    };

  export const KerfWidthMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.KERF_WIDTH,
      apiName: 'kerfWidth',
      name: 'cutting:kerf_width',
      displayOrder: 90,
      tags: [FlowItemParameterTag.APPROACH],
      ui: {
        widget: WidgetType.NUMBER,
        widgetOptions: {
          step: 0.001,
        },
      },
      value: {
        type: 'number',
        minimum: 0.1,
        maximum: 5.1,
        required: true,
        uom: {
          target: 'mm',
          system: {
            imperial: 'in',
            metric: 'mm',
          },
          precision: 3,
        },
      },
    };

  export const KerfDirectionMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.KERF_DIRECTION,
      displayOrder: 100,
      apiName: 'kerfDirection',
      name: 'cutting:kerf_direction',
      tags: [FlowItemParameterTag.APPROACH],
      ui: {
        widget: WidgetType.PICKER,
        widgetOptions: [
          {
            label: 'cutting:left',
            value: KerfDirection.LEFT,
          },
          {
            label: 'cutting:right',
            value: KerfDirection.RIGHT,
          },
        ],
      },
      value: {
        type: 'string',
        default: KerfDirection.LEFT,
        required: true,
      },
    };

  export const CutHeightMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.CUT_HEIGHT,
      apiName: 'cutHeight',
      name: 'cutting:cut_height',
      tags: [FlowItemParameterTag.APPROACH],
      displayOrder: 30,
      ui: {
        widget: WidgetType.NUMBER,
        widgetOptions: {
          step: 0.001,
        },
      },
      value: {
        type: 'number',
        minimum: 0.5,
        maximum: 6.4,
        required: true,
        uom: {
          target: 'mm',
          system: {
            imperial: 'in',
            metric: 'mm',
          },
          precision: 3,
        },
      },
    };

  export const PierceHeightMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.PIERCE_HEIGHT,
      apiName: 'pierceHeight',
      name: 'cutting:pierce_height',
      tags: [FlowItemParameterTag.APPROACH],
      displayOrder: 30,
      ui: {
        widget: WidgetType.NUMBER,
        widgetOptions: {
          step: 0.001,
        },
      },
      value: {
        type: 'number',
        minimum: 0,
        maximum: 7.9,
        required: true,
        uom: {
          target: 'mm',
          system: {
            imperial: 'in',
            metric: 'mm',
          },
          precision: 3,
        },
      },
    };

  export const HyperthermPowermax125PierceHeight: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ...PierceHeightMetadata,
      value: {
        ...PierceHeightMetadata.value,
        maximum: 11.43,
      },
    };

  export const PierceDelayMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.PIERCE_DELAY,
      apiName: 'pierceDelay',
      name: 'cutting:pierce_delay',
      tags: [FlowItemParameterTag.APPROACH],
      displayOrder: 40,
      ui: {
        widget: WidgetType.NUMBER,
        widgetOptions: {
          step: 0.1,
        },
      },
      value: {
        type: 'number',
        minimum: 0,
        maximum: 2,
        required: true,
      },
    };

  export const TorchHeightControlEnabled: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.TORCH_HEIGHT_CONTROL_ENABLED,
      apiName: 'torchHeightControlEnabled',
      name: 'cutting:torch_height_control_enabled',
      tags: [FlowItemParameterTag.APPROACH],
      displayOrder: 100,
      ui: {
        widget: WidgetType.SWITCH,
      },
      value: {
        type: 'boolean',
        default: true,
      },
    };

  export const TouchSurfaceEnabled: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.TOUCH_SURFACE_ENABLED,
      apiName: 'touchSurfaceEnabled',
      name: 'cutting:touch_surface_enabled',
      tags: [FlowItemParameterTag.APPROACH],
      displayOrder: 50,
      ui: {
        widget: WidgetType.HIDDEN,
      },
      value: {
        type: 'boolean',
        default: true,
      },
    };

  export const ApproachMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.APPROACH,
      apiName: 'approach',
      name: 'cutting:approach_cut',
      value: {
        type: 'boolean',
        default: false,
      },
    };

  export const DepartMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.DEPART,
      apiName: 'depart',
      name: 'cutting:depart_cut',
      value: {
        type: 'boolean',
        default: false,
      },
    };

  export const TravelSpeedMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.TRAVEL_SPEED,
      apiName: 'travelSpeed',
      name: 'cutting:cut_speed',
      tags: [
        FlowItemParameterTag.PROCESS_CUT,
        FlowItemParameterTag.PROCESS_GOUGE,
      ],
      displayOrder: 25,
      ui: {
        widget: WidgetType.NUMBER,
        widgetOptions: {
          step: 0.1,
        },
      },
      value: {
        type: 'number',
        required: true,
        minimum: 1.17,
        maximum: 212.8,
        uom: {
          target: 'mm/s',
          system: {
            imperial: 'in/min',
            metric: 'cm/min',
          },
          precision: 3,
        },
      },
    };

  export const QNearMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.QNEAR,
      apiName: 'qnear',
      tags: [
        FlowItemParameterTag.PROCESS_CUT,
        FlowItemParameterTag.PROCESS_GOUGE,
      ],
      value: {
        type: 'array',
        required: true,
      },
    };

  export const WaypointMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.WAYPOINT,
      apiName: 'waypoint',
      name: 'cutting:waypoint',
      tags: [
        FlowItemParameterTag.PROCESS_CUT,
        FlowItemParameterTag.PROCESS_GOUGE,
      ],
      value: WaypointParameterValue,
    };

  export const CutTypeMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.CUT_TYPE,
      apiName: 'motionType',
      name: 'cutting:cut_type',
      tags: [
        FlowItemParameterTag.PROCESS_CUT,
        FlowItemParameterTag.PROCESS_GOUGE,
        FlowItemParameterTag.APPROACH,
        FlowItemParameterTag.DEPART,
      ],
      description: 'cutting:cut_type_desc',
      displayOrder: 1,
      ui: {
        widget: WidgetType.PICKER,
        widgetOptions: [
          {
            label: 'cutting:cut_type_linear',
            value: CutTypeEnum.LINEAR,
          },
          {
            label: 'cutting:cut_type_radial',
            value: CutTypeEnum.RADIAL,
          },
          {
            label: 'cutting:cut_type_rotary',
            value: CutTypeEnum.ROTARY,
          },
        ],
      },
      value: {
        type: 'number',
        default: 1,
      },
    };

  export const MoveMotionTypeMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.MOVE_MOTION_TYPE,
      apiName: 'motionType',
      name: 'cutting:motion_type',
      value: {
        type: 'number',
        default: 0,
      },
      displayOrder: 1,
      ui: {
        widget: WidgetType.HIDDEN,
      },
    };

  export const MoveSpeedAdjustmentRatioMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.MOVE_SPEED_ADJUSTMENT_RATIO,
    apiName: 'moveSpeedAdjustmentRatio',
    name: 'welding:move_speed_adjustment_ratio',
    displayOrder: 10,
    description: 'welding:move_speed_adjustment_ratio_desc',
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 0.25,
      },
    },
    value: {
      type: 'number',
      required: true,
      minimum: 0.25,
      maximum: 1,
      default: 1,
    },
  };

  export const MoveTravelSpeedMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.MOVE_TRAVEL_SPEED,
      apiName: 'travelSpeed',
      name: 'cutting:travel_speed',
      value: {
        type: 'number',
        default: 0.03,
      },
    };

  export const CutBlendRadiusMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.CUT_BLEND_RADIUS,
      apiName: 'blendRadius',
      displayOrder: 26,
      name: 'cutting:blend_radius',
      tags: [FlowItemParameterTag.PROCESS_CUT],
      value: {
        type: 'number',
        default: 1.5,
        minimum: 0,
        maximum: 50,
        required: true,
        uom: {
          target: 'mm',
        },
      },
      ui: {
        widget: WidgetType.NUMBER,
        widgetOptions: {
          step: 0.1,
        },
      },
    };

  export const MoveBlendRadiusMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.MOVE_BLEND_RADIUS,
      apiName: 'blendRadius',
      displayOrder: 10,
      name: 'cutting:blend_radius',
      value: {
        type: 'number',
        default: 50,
        minimum: 0,
        maximum: 100,
        required: true,
      },
      ui: {
        widget: WidgetType.HIDDEN,
      },
    };

  export const ProductionQuanityMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.PRODUCTION_QUANTITY,
      apiName: 'quantity',
      displayOrder: 0,
      name: 'common:quantity',
      value: {
        type: 'number',
        default: 1,
        required: true,
      },
      ui: {
        widget: WidgetType.NUMBER,
      },
    };

  export const TriggerOutputPortMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.TRIGGER_OUTPUT_PORT,
      apiName: 'outputPort',
      displayOrder: 0,
      name: 'cutting:trigger_output',
      value: {
        type: 'number',
        default: 24,
        required: true,
      },
      ui: {
        widget: WidgetType.PICKER,
        widgetOptions: [
          {
            label: 'common:color_red',
            value: 24,
          },
          {
            label: 'common:color_blue',
            value: 25,
          },
          {
            label: 'common:color_yellow',
            value: 26,
          },
          {
            label: 'common:color_green',
            value: 27,
          },
        ],
      },
    };

  export const TriggerOutputStateMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.TRIGGER_OUTPUT_STATE,
      apiName: 'outputState',
      displayOrder: 0,
      name: 'common:state',
      value: {
        type: 'boolean',
        default: true,
      },
      ui: {
        widget: WidgetType.PICKER,
        widgetOptions: [
          {
            label: 'common:on',
            value: true,
          },
          {
            label: 'common:off',
            value: false,
          },
        ],
      },
    };

  export const RotaryTriggerOutputPortMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    assign({}, TriggerOutputPortMetadata, {
      tags: [FlowItemParameterTag.ROTARY],
      displayOrder: 1.1,
    } as FlowItemParameterMetadata<FlowItemParameterTag>);

  export const WaitForOperatorMessageMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.WAIT_FOR_OPERATOR_MESSAGE,
      apiName: 'message',
      tags: [FlowItemParameterTag.WAIT_FOR_OPERATOR],
      displayOrder: 100,
      name: 'common:message',
      value: {
        type: 'string',
        default: i18n.t('cutting:wait_for_operator_default_message'),
        required: false,
      },
      ui: {
        widget: WidgetType.TEXT,
      },
    };

  export enum WaitForInputType {
    OPERATOR_BUTTON = 'OPERATOR_BUTTON',
    SENSOR = 'SENSOR',
    TIME = 'TIME',
  }

  export const WaitForTypeMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.WAIT_FOR_TYPE_OF_INPUT,
      apiName: 'typeOfInput',
      displayOrder: 0,
      name: 'cutting:wait_for',
      value: {
        type: 'string',
        default: WaitForInputType.OPERATOR_BUTTON,
        required: false,
      },
      ui: {
        widget: WidgetType.PICKER,
        widgetOptions: [
          {
            label: 'common:operator',
            value: WaitForInputType.OPERATOR_BUTTON,
          },
          {
            label: 'common:input',
            value: WaitForInputType.SENSOR,
          },
          {
            label: 'common:time',
            value: WaitForInputType.TIME,
          },
        ],
      },
    };

  export const WaitForInputMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.WAIT_FOR_INPUT,
      tags: [FlowItemParameterTag.WAIT_FOR_INPUT],
      apiName: 'inputPort',
      displayOrder: 0,
      name: 'common:input',
      value: {
        type: 'number',
        default: 16,
        required: true,
      },
      ui: {
        widget: WidgetType.PICKER,
        widgetOptions: [
          {
            label: 'common:color_red',
            value: 16,
          },
          {
            label: 'common:color_blue',
            value: 17,
          },
          {
            label: 'common:color_yellow',
            value: 18,
          },
          {
            label: 'common:color_green',
            value: 19,
          },
        ],
      },
    };

  export const WaitForInputStateMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.WAIT_FOR_INPUT_STATE,
      tags: [FlowItemParameterTag.WAIT_FOR_INPUT],
      apiName: 'waitForState',
      displayOrder: 0,
      name: 'common:state',
      value: {
        type: 'boolean',
        default: true,
      },
      ui: {
        widget: WidgetType.PICKER,
        widgetOptions: [
          {
            label: 'common:on',
            value: true,
          },
          {
            label: 'common:off',
            value: false,
          },
        ],
      },
    };

  export const WaitForTimeMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.WAIT_FOR_TIME,
      apiName: 'inputPort',
      tags: [FlowItemParameterTag.WAIT_FOR_TIME],
      displayOrder: 0,
      name: 'common:time_seconds',
      value: {
        type: 'number',
        default: 5,
      },
      ui: {
        widget: WidgetType.NUMBER,
        widgetOptions: {
          step: 0.01,
        },
      },
    };

  export const RotaryWaitForTypeMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    assign({}, WaitForTypeMetadata, {
      tags: [FlowItemParameterTag.ROTARY],
      displayOrder: 1.01,
      value: {
        ...WaitForTypeMetadata.value,
        default: WaitForInputType.SENSOR,
      },
      ui: {
        ...WaitForTypeMetadata.ui,
        widgetOptions: (
          WaitForTypeMetadata.ui as PickerWidget
        )?.widgetOptions.filter(
          (opt) => opt.value !== WaitForInputType.OPERATOR_BUTTON
        ),
      },
    } as FlowItemParameterMetadata);

  export const RotaryWaitForInputMetadata: FlowItemParameterMetadata = assign(
    {},
    WaitForInputMetadata,
    {
      displayOrder: 1.02,
    } as FlowItemParameterMetadata
  );

  export const RotaryWaitForInputStateMetadata: FlowItemParameterMetadata =
    assign({}, WaitForInputStateMetadata, {
      displayOrder: 1.03,
    } as FlowItemParameterMetadata);

  export const RotaryWaitForTimeMetadata: FlowItemParameterMetadata = assign(
    {},
    WaitForTimeMetadata,
    {
      displayOrder: 1.04,
    } as FlowItemParameterMetadata
  );

  export const SectionColorParameterMetadata: FlowItemParameterMetadata<FlowItemParameterTag> =
    {
      ref: FlowItemParameterRef.SECTION_COLOR,
      name: 'common:color',
      displayOrder: 10,
      apiName: 'color',
      value: {
        type: 'string',
      },
      ui: {
        widget: WidgetType.COLOR_PICKER,
      },
    };

  export const FlowItemCutParametersMetadata = [
    ApproachMetadata,
    CutHeightMetadata,
    CutTypeMetadata,
    DepartMetadata,
    KerfDirectionMetadata,
    KerfWidthMetadata,
    PierceDelayMetadata,
    PierceHeightMetadata,
    ProcessMetadata,
    QNearMetadata,
    RotaryWaitForTypeMetadata,
    TorchHeightControlEnabled,
    TouchSurfaceEnabled,
    TravelSpeedMetadata,
    CutBlendRadiusMetadata,
    TriggerOutputPortMetadata,
    VoltageMetadata,
    WaitForInputMetadata,
    WaitForInputStateMetadata,
    WaitForTimeMetadata,
    WaypointMetadata,
  ];

  export const FlowItemMoveParametersMetadata: FlowItemParameterMetadata<FlowItemParameterTag>[] =
    [
      MoveBlendRadiusMetadata,
      MoveMotionTypeMetadata,
      MoveSpeedAdjustmentRatioMetadata,
      MoveTravelSpeedMetadata,
      QNearMetadata,
      WaypointMetadata,
    ];

  export const FlowItemWaitForParametersMetadata: FlowItemParameterMetadata<FlowItemParameterTag>[] =
    [
      WaitForTypeMetadata,
      WaitForInputMetadata,
      WaitForTimeMetadata,
      WaitForOperatorMessageMetadata,
      WaitForInputStateMetadata,
    ];

  export const FlowItemTriggerOutputParametersMetadata: FlowItemParameterMetadata<FlowItemParameterTag>[] =
    [TriggerOutputPortMetadata, TriggerOutputStateMetadata];

  export const FlowItemSectionParametersMetadata: FlowItemParameterMetadata<FlowItemParameterTag>[] =
    [SectionColorParameterMetadata];

  export const BeaconStartItem: FlowItemMetadata = {
    ref: FlowItemRef.START,
    codeBlock: 0,
    name: 'common:start',
    description: 'cutting:start_desc',
    icon: home,
    displayOrder: 0,
    availableParameters: [],
  };

  export const BeaconEndItem: FlowItemMetadata = {
    ref: FlowItemRef.END,
    codeBlock: 10000,
    name: 'common:end',
    description: 'cutting:end_desc',
    icon: flag,
    displayOrder: 10000,
    availableParameters: [],
  };

  export const BeaconSectionItem: FlowItemMetadata = {
    ref: FlowItemRef.SECTION,
    codeBlock: 6,
    name: 'common:section',
    description: 'cutting:section_desc',
    icon: flag,
    displayOrder: 10000,
    availableParameters: [...FlowItemSectionParametersMetadata],
    hidden: true,
  };

  export const BeaconMoveItem: FlowItemMetadata = {
    ref: FlowItemRef.MOVE,
    codeBlock: 2,
    name: 'cutting:move',
    description: 'cutting:move_desc',
    icon: moveItem,
    displayOrder: 20,
    availableParameters: [...FlowItemMoveParametersMetadata],
  };

  export const BeaconCutItem: FlowItemMetadata = {
    ref: FlowItemRef.CUT,
    codeBlock: 1,
    name: 'cutting:cut',
    description: 'cutting:cut_desc',
    icon: cutOutline,
    displayOrder: 10,
    availableParameters: [...FlowItemCutParametersMetadata],
  };

  export const BeaconMoveHomeItem: FlowItemMetadata = {
    ref: FlowItemRef.MOVE_HOME,
    codeBlock: 2,
    name: 'cutting:move_home',
    description: 'cutting:move_home_desc',
    icon: homeOutline,
    displayOrder: 30,
    availableParameters: [],
  };

  export const BeaconWaitForItem: FlowItemMetadata = {
    ref: FlowItemRef.WAIT_FOR,
    codeBlock: 3,
    name: 'cutting:wait_for',
    description: 'cutting:wait_for_desc',
    icon: waitForOperator,
    displayOrder: 40,
    availableParameters: [...FlowItemWaitForParametersMetadata],
  };

  export const BeaconReportProductionItem: FlowItemMetadata = {
    ref: FlowItemRef.PRODUCTION_REPORTING,
    codeBlock: 4,
    name: 'cutting:production_reporting',
    description: 'cutting:production_reporting_desc',
    icon: reportProduction,
    displayOrder: 50,
    availableParameters: [ProductionQuanityMetadata],
  };

  export const BeaconTriggerOutputItem: FlowItemMetadata = {
    ref: FlowItemRef.TRIGGER_OUTPUT,
    codeBlock: 5,
    name: 'cutting:trigger_output',
    description: 'cutting:trigger_output_desc',
    icon: pushOutline,
    displayOrder: 60,
    availableParameters: FlowItemTriggerOutputParametersMetadata,
    articleId: IntercomArticle.INTEGRATE_WITH_EXTERNAL_EQUIPMENT,
  };

  export const BeaconFlowItemsMetadata = [
    BeaconCutItem,
    BeaconMoveItem,
    BeaconMoveHomeItem,
    BeaconReportProductionItem,
    BeaconSectionItem,
    BeaconTriggerOutputItem,
    BeaconWaitForItem,
  ];

  export const CommonItems: FlowItemMetadata[] = [
    BeaconMoveItem,
    BeaconMoveHomeItem,
    BeaconReportProductionItem,
    BeaconSectionItem,
    BeaconTriggerOutputItem,
    BeaconWaitForItem,
  ];

  type FlowItemByCutterMapping = {
    [key in Cutter]: FlowItemMetadata[];
  };

  export const FlowItemsByCutter: FlowItemByCutterMapping = {
    [Cutter.HYPERTHERM_POWERMAX_SYNC]: BeaconFlowItemsMetadata,
    [Cutter.HYPERTHERM_POWERMAX_125]: [
      ...CommonItems,
      {
        ...BeaconCutItem,
        availableParameters: [
          ...BeaconCutItem.availableParameters.filter(
            (p) =>
              ![FlowItemParameterRef.PIERCE_HEIGHT].includes(
                p.ref as FlowItemParameterRef
              )
          ),
          HyperthermPowermax125PierceHeight,
        ],
      },
    ],
    [Cutter.THERMAL_DYNAMICS_CUTMASTER]: BeaconFlowItemsMetadata,
  };

  export enum ToolKey {
    HYPERTHERM_45 = 'hypertherm_45',
    HYPERTHERM_0 = 'hypertherm_0',
    HYPERTHERM_HYAMP_45 = 'hypertherm_hyamp_45',
    THERMAL_DYNAMICS_0 = 'thermal_dynamics_0',
  }

  export const Tools: Tool[] = [
    {
      id: ToolKey.HYPERTHERM_45,
      label: 'Hypertherm 45º',
      config: {
        cog: [-0.008, -0.031, 0.116],
        inertia: [0.003404, 0.003404, 0.003404, 0, 0, 0],
        mass: 1.61,
        tcp: {
          rx: 0,
          ry: 0,
          rz: 0,
          x: 0,
          y: 0,
          z: 0.315,
        },
      },
      equipment: [Cutter.HYPERTHERM_POWERMAX_SYNC],
      models: [URModel.UR5, URModel.UR8_LONG, URModel.UR10],
    },
    {
      id: ToolKey.HYPERTHERM_45,
      label: 'Hypertherm 45º',
      config: {
        cog: [-0.008, -0.031, 0.116],
        inertia: [0.003404, 0.003404, 0.003404, 0, 0, 0],
        mass: 2,
        tcp: {
          rx: 0,
          ry: 0,
          rz: 0,
          x: 0,
          y: -0.00175,
          z: 0.333,
        },
      },
      equipment: [Cutter.HYPERTHERM_POWERMAX_SYNC],
      models: [URModel.UR20],
    },
    {
      id: ToolKey.HYPERTHERM_0,
      label: 'Hypertherm 0º',
      config: {
        cog: [-0.008, -0.031, 0.116],
        inertia: [0.003404, 0.003404, 0.003404, 0, 0, 0],
        mass: 1.61,
        tcp: {
          rx: -0.78,
          ry: 0,
          rz: 0,
          x: 0.0044,
          y: 0.08824,
          z: 0.3271,
        },
      },
      equipment: [Cutter.HYPERTHERM_POWERMAX_SYNC],
      models: [URModel.UR5, URModel.UR8_LONG, URModel.UR10],
    },
    {
      id: ToolKey.HYPERTHERM_HYAMP_45,
      label: 'Hypertherm HyAmp 45º',
      config: {
        cog: [0.004, -0.017, 0.141],
        inertia: [0.004887, 0, 0, 0, 0.004887, 0],
        mass: 2,
        tcp: {
          rx: 0,
          ry: 0,
          rz: 0,
          x: 0.0007,
          y: 0.0139,
          z: 0.354,
        },
      },
      equipment: [Cutter.HYPERTHERM_POWERMAX_125],
      models: [URModel.UR5, URModel.UR8_LONG, URModel.UR10],
    },
    {
      id: ToolKey.HYPERTHERM_HYAMP_45,
      label: 'Hypertherm HyAmp 45º',
      config: {
        cog: [0, -0.037, 0.109],
        inertia: [0.004887, 0, 0, 0, 0.004887, 0],
        mass: 2.5,
        tcp: {
          rx: 0,
          ry: 0,
          rz: 0,
          x: 0.0005,
          y: 0.0118,
          z: 0.372,
        },
      },
      equipment: [Cutter.HYPERTHERM_POWERMAX_125],
      models: [URModel.UR20],
    },
    {
      id: ToolKey.THERMAL_DYNAMICS_0,
      label: 'Thermal Dynamics 0º',
      config: {
        cog: [-0.021, -0.101, 0.122],
        inertia: [0.007042, 0.007042, 0.007042, 0, 0, 0],
        mass: 2.4,
        tcp: {
          rx: -0.3682,
          ry: 0.0039,
          rz: 0.0632,
          x: -0.00071,
          y: -0.00651,
          z: 0.309,
        },
      },
      equipment: [Cutter.THERMAL_DYNAMICS_CUTMASTER],
      models: [URModel.UR5, URModel.UR8_LONG, URModel.UR10, URModel.UR20],
    },
  ];

  export interface GetParameterMetadataFromFormOptions {
    forceCutType?: CutTypeEnum;
    forceDepart?: boolean;
    forceApproach?: boolean;
    forceIncludeTags?: FlowItemParameterTag[];
    forceExcludeRefs?: FlowItemParameterRef[];
  }

  export const DefaultCutParameterMetadataFromFormOptions: GetParameterMetadataFromFormOptions =
    {
      forceCutType: CutTypeEnum.LINEAR,
      forceIncludeTags: [
        FlowItemParameterTag.DEPART,
        FlowItemParameterTag.APPROACH,
      ],
      forceExcludeRefs: [
        FlowItemParameterRef.CUT_TYPE,
        FlowItemParameterRef.QNEAR,
        FlowItemParameterRef.WAYPOINT,
      ],
      forceApproach: false,
      forceDepart: false,
    };

  export const HyperthermPowermaxSyncParameterSetTags: ApplicationDefaultParameterSetTagData =
    {
      [ApplicationDefaultParameterSetTag.CURRENT]: {
        order: 10,
        required: true,
        label: 'cutting:current',
        options: [
          {
            label: 'cutting:current_105A',
            value: Current.A_105,
          },
          {
            label: 'cutting:current_85A',
            value: Current.A_85,
          },
          {
            label: 'cutting:current_65A',
            value: Current.A_65,
          },
          {
            label: 'cutting:current_45A',
            value: Current.A_45,
          },
          {
            label: 'cutting:current_40A',
            value: Current.A_40,
          },
          {
            label: 'cutting:current_35A',
            value: Current.A_35,
          },
          {
            label: 'cutting:current_30A',
            value: Current.A_30,
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.GAS]: {
        order: 20,
        required: true,
        label: 'cutting:gas',
        options: [
          {
            label: 'cutting:gas_air',
            value: Gas.AIR,
          },
          {
            label: 'cutting:gas_f5',
            value: Gas.F5,
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.MATERIAL_TYPE]: {
        order: 30,
        required: true,
        label: 'cutting:material_type',
        options: [
          {
            label: 'cutting:material_steel',
            value: Material.STEEL,
          },
          {
            label: 'cutting:material_stainless',
            value: Material.STAINLESS,
          },
          {
            label: 'cutting:material_aluminum',
            value: Material.ALUMINUM,
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.MATERIAL_THICKNESS]: {
        order: 40,
        required: true,
        label: 'cutting:material_thickness',
        options: [
          {
            label: 'cutting:material_thickness_00.5',
            searchTerms: ['26'],
            value: MaterialThickness['26GA'],
          },
          {
            label: 'cutting:material_thickness_00.6',
            searchTerms: ['24'],
            value: MaterialThickness['24GA'],
          },
          {
            label: 'cutting:material_thickness_00.8',
            searchTerms: ['22'],
            value: MaterialThickness['22GA'],
          },
          {
            label: 'cutting:material_thickness_00.9',
            searchTerms: ['20'],
            value: MaterialThickness['20GA'],
          },
          {
            label: 'cutting:material_thickness_01.2',
            searchTerms: ['18'],
            value: MaterialThickness['18GA'],
          },
          {
            label: 'cutting:material_thickness_01.6',
            searchTerms: ['16'],
            value: MaterialThickness['16GA'],
          },
          {
            label: 'cutting:material_thickness_02.0',
            searchTerms: ['14'],
            value: MaterialThickness['14GA'],
          },
          {
            label: 'cutting:material_thickness_02.8',
            searchTerms: ['12'],
            value: MaterialThickness['12GA'],
          },
          {
            label: 'cutting:material_thickness_03.6',
            searchTerms: ['10'],
            value: MaterialThickness['10GA'],
          },
          {
            label: 'cutting:material_thickness_02.4',
            searchTerms: ['3/32'],
            value: MaterialThickness['3/32'],
          },
          {
            label: 'cutting:material_thickness_03.2',
            searchTerms: ['1/8'],
            value: MaterialThickness['1/8'],
          },
          {
            label: 'cutting:material_thickness_04.8',
            searchTerms: ['3/16'],
            value: MaterialThickness['3/16'],
          },
          {
            label: 'cutting:material_thickness_06.4',
            searchTerms: ['1/4'],
            value: MaterialThickness['1/4'],
          },
          {
            label: 'cutting:material_thickness_07.9',
            searchTerms: ['5/16'],
            value: MaterialThickness['5/16'],
          },
          {
            label: 'cutting:material_thickness_09.5',
            searchTerms: ['3/8'],
            value: MaterialThickness['3/8'],
          },
          {
            label: 'cutting:material_thickness_12.7',
            searchTerms: ['1/2'],
            value: MaterialThickness['1/2'],
          },
          {
            label: 'cutting:material_thickness_15.9',
            searchTerms: ['5/8'],
            value: MaterialThickness['5/8'],
          },
          {
            label: 'cutting:material_thickness_19.1',
            searchTerms: ['3/4'],
            value: MaterialThickness['3/4'],
          },
          {
            label: 'cutting:material_thickness_22.2',
            searchTerms: ['7/8'],
            value: MaterialThickness['7/8'],
          },
          {
            label: 'cutting:material_thickness_25.4',
            searchTerms: ['1_0'],
            value: MaterialThickness['1_0'],
          },
          {
            label: 'cutting:material_thickness_28.6',
            searchTerms: ['1/8'],
            value: MaterialThickness['1_1/8'],
          },
          {
            label: 'cutting:material_thickness_31.8',
            searchTerms: ['1/4'],
            value: MaterialThickness['1_1/4'],
          },
          {
            label: 'cutting:material_thickness_34.9',
            searchTerms: ['3/8'],
            value: MaterialThickness['1_3/8'],
          },
          {
            label: 'cutting:material_thickness_38.1',
            searchTerms: ['1/2'],
            value: MaterialThickness['1_1/2'],
          },
          {
            label: 'cutting:material_thickness_41.3',
            searchTerms: ['5/8'],
            value: MaterialThickness['1_5/8'],
          },
        ],
      },
    };

  export const HyperthermPowermax125ParameterSetTags: ApplicationDefaultParameterSetTagData =
    {
      [ApplicationDefaultParameterSetTag.CURRENT]: {
        order: 10,
        required: true,
        label: 'cutting:current',
        options: [
          {
            label: 'cutting:current_125A',
            value: Current.A_125,
          },
          {
            label: 'cutting:current_105A',
            value: Current.A_105,
          },
          {
            label: 'cutting:current_85A',
            value: Current.A_85,
          },
          {
            label: 'cutting:current_65A',
            value: Current.A_65,
          },
          {
            label: 'cutting:current_45A',
            value: Current.A_45,
          },
          {
            label: 'cutting:current_40A',
            value: Current.A_40,
          },
          {
            label: 'cutting:current_35A',
            value: Current.A_35,
          },
          {
            label: 'cutting:current_30A',
            value: Current.A_30,
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.GAS]: {
        order: 20,
        required: true,
        label: 'cutting:gas',
        options: [
          {
            label: 'cutting:gas_air',
            value: Gas.AIR,
          },
          {
            label: 'cutting:gas_f5',
            value: Gas.F5,
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.MATERIAL_TYPE]: {
        order: 30,
        required: true,
        label: 'cutting:material_type',
        options: [
          {
            label: 'cutting:material_steel',
            value: Material.STEEL,
          },
          {
            label: 'cutting:material_stainless',
            value: Material.STAINLESS,
          },
          {
            label: 'cutting:material_aluminum',
            value: Material.ALUMINUM,
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.MATERIAL_THICKNESS]: {
        order: 40,
        required: true,
        label: 'cutting:material_thickness',
        options: [
          {
            label: 'cutting:material_thickness_00.5',
            searchTerms: ['26'],
            value: MaterialThickness['26GA'],
          },
          {
            label: 'cutting:material_thickness_00.6',
            searchTerms: ['24'],
            value: MaterialThickness['24GA'],
          },
          {
            label: 'cutting:material_thickness_00.8',
            searchTerms: ['22'],
            value: MaterialThickness['22GA'],
          },
          {
            label: 'cutting:material_thickness_00.9',
            searchTerms: ['20'],
            value: MaterialThickness['20GA'],
          },
          {
            label: 'cutting:material_thickness_01.2',
            searchTerms: ['18'],
            value: MaterialThickness['18GA'],
          },
          {
            label: 'cutting:material_thickness_01.6',
            searchTerms: ['16'],
            value: MaterialThickness['16GA'],
          },
          {
            label: 'cutting:material_thickness_02.0',
            searchTerms: ['14'],
            value: MaterialThickness['14GA'],
          },
          {
            label: 'cutting:material_thickness_02.8',
            searchTerms: ['12'],
            value: MaterialThickness['12GA'],
          },
          {
            label: 'cutting:material_thickness_03.6',
            searchTerms: ['10'],
            value: MaterialThickness['10GA'],
          },
          {
            label: 'cutting:material_thickness_02.4',
            searchTerms: ['3/32'],
            value: MaterialThickness['3/32'],
          },
          {
            label: 'cutting:material_thickness_03.2',
            searchTerms: ['1/8'],
            value: MaterialThickness['1/8'],
          },
          {
            label: 'cutting:material_thickness_04.8',
            searchTerms: ['3/16'],
            value: MaterialThickness['3/16'],
          },
          {
            label: 'cutting:material_thickness_06.4',
            searchTerms: ['1/4'],
            value: MaterialThickness['1/4'],
          },
          {
            label: 'cutting:material_thickness_07.9',
            searchTerms: ['5/16'],
            value: MaterialThickness['5/16'],
          },
          {
            label: 'cutting:material_thickness_09.5',
            searchTerms: ['3/8'],
            value: MaterialThickness['3/8'],
          },
          {
            label: 'cutting:material_thickness_12.7',
            searchTerms: ['1/2'],
            value: MaterialThickness['1/2'],
          },
          {
            label: 'cutting:material_thickness_15.9',
            searchTerms: ['5/8'],
            value: MaterialThickness['5/8'],
          },
          {
            label: 'cutting:material_thickness_19.1',
            searchTerms: ['3/4'],
            value: MaterialThickness['3/4'],
          },
          {
            label: 'cutting:material_thickness_22.2',
            searchTerms: ['7/8'],
            value: MaterialThickness['7/8'],
          },
          {
            label: 'cutting:material_thickness_25.4',
            searchTerms: ['1_0'],
            value: MaterialThickness['1_0'],
          },
          {
            label: 'cutting:material_thickness_28.6',
            searchTerms: ['1/8'],
            value: MaterialThickness['1_1/8'],
          },
          {
            label: 'cutting:material_thickness_31.8',
            searchTerms: ['1/4'],
            value: MaterialThickness['1_1/4'],
          },
          {
            label: 'cutting:material_thickness_34.9',
            searchTerms: ['3/8'],
            value: MaterialThickness['1_3/8'],
          },
          {
            label: 'cutting:material_thickness_38.1',
            searchTerms: ['1/2'],
            value: MaterialThickness['1_1/2'],
          },
          {
            label: 'cutting:material_thickness_41.3',
            searchTerms: ['5/8'],
            value: MaterialThickness['1_5/8'],
          },
        ],
      },
    };

  export const ThermalDynamicsCutmasterParameterSetTags: ApplicationDefaultParameterSetTagData =
    {
      [ApplicationDefaultParameterSetTag.CURRENT]: {
        order: 10,
        required: true,
        label: 'cutting:current',
        options: [
          {
            label: 'cutting:current_120A',
            value: Current.A_120,
          },
          {
            label: 'cutting:current_100A',
            value: Current.A_100,
          },
          {
            label: 'cutting:current_80A',
            value: Current.A_80,
          },
          {
            label: 'cutting:current_60A',
            value: Current.A_60,
          },
          {
            label: 'cutting:current_40A',
            value: Current.A_40,
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.GAS]: {
        order: 20,
        required: true,
        label: 'cutting:gas',
        options: [
          {
            label: 'cutting:gas_air',
            value: Gas.AIR,
          },
          {
            label: 'cutting:gas_f5',
            value: Gas.F5,
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.MATERIAL_TYPE]: {
        order: 30,
        required: true,
        label: 'cutting:material_type',
        options: [
          {
            label: 'cutting:material_steel',
            value: Material.STEEL,
          },
          {
            label: 'cutting:material_stainless',
            value: Material.STAINLESS,
          },
          {
            label: 'cutting:material_aluminum',
            value: Material.ALUMINUM,
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.MATERIAL_THICKNESS]: {
        order: 40,
        required: true,
        label: 'cutting:material_thickness',
        options: [
          {
            label: 'cutting:material_thickness_00.5',
            searchTerms: ['26'],
            value: MaterialThickness['26GA'],
          },
          {
            label: 'cutting:material_thickness_00.6',
            searchTerms: ['24'],
            value: MaterialThickness['24GA'],
          },
          {
            label: 'cutting:material_thickness_00.8',
            searchTerms: ['22'],
            value: MaterialThickness['22GA'],
          },
          {
            label: 'cutting:material_thickness_00.9',
            searchTerms: ['20'],
            value: MaterialThickness['20GA'],
          },
          {
            label: 'cutting:material_thickness_01.2',
            searchTerms: ['18'],
            value: MaterialThickness['18GA'],
          },
          {
            label: 'cutting:material_thickness_01.6',
            searchTerms: ['16'],
            value: MaterialThickness['16GA'],
          },
          {
            label: 'cutting:material_thickness_02.0',
            searchTerms: ['14'],
            value: MaterialThickness['14GA'],
          },
          {
            label: 'cutting:material_thickness_02.8',
            searchTerms: ['12'],
            value: MaterialThickness['12GA'],
          },
          {
            label: 'cutting:material_thickness_03.6',
            searchTerms: ['10'],
            value: MaterialThickness['10GA'],
          },
          {
            label: 'cutting:material_thickness_02.4',
            searchTerms: ['3/32'],
            value: MaterialThickness['3/32'],
          },
          {
            label: 'cutting:material_thickness_03.2',
            searchTerms: ['1/8'],
            value: MaterialThickness['1/8'],
          },
          {
            label: 'cutting:material_thickness_04.8',
            searchTerms: ['3/16'],
            value: MaterialThickness['3/16'],
          },
          {
            label: 'cutting:material_thickness_06.4',
            searchTerms: ['1/4'],
            value: MaterialThickness['1/4'],
          },
          {
            label: 'cutting:material_thickness_07.9',
            searchTerms: ['5/16'],
            value: MaterialThickness['5/16'],
          },
          {
            label: 'cutting:material_thickness_09.5',
            searchTerms: ['3/8'],
            value: MaterialThickness['3/8'],
          },
          {
            label: 'cutting:material_thickness_12.7',
            searchTerms: ['1/2'],
            value: MaterialThickness['1/2'],
          },
          {
            label: 'cutting:material_thickness_15.9',
            searchTerms: ['5/8'],
            value: MaterialThickness['5/8'],
          },
          {
            label: 'cutting:material_thickness_19.1',
            searchTerms: ['3/4'],
            value: MaterialThickness['3/4'],
          },
          {
            label: 'cutting:material_thickness_22.2',
            searchTerms: ['7/8'],
            value: MaterialThickness['7/8'],
          },
          {
            label: 'cutting:material_thickness_25.4',
            searchTerms: ['1_0'],
            value: MaterialThickness['1_0'],
          },
          {
            label: 'cutting:material_thickness_28.6',
            searchTerms: ['1/8'],
            value: MaterialThickness['1_1/8'],
          },
          {
            label: 'cutting:material_thickness_31.8',
            searchTerms: ['1/4'],
            value: MaterialThickness['1_1/4'],
          },
          {
            label: 'cutting:material_thickness_34.9',
            searchTerms: ['3/8'],
            value: MaterialThickness['1_3/8'],
          },
          {
            label: 'cutting:material_thickness_38.1',
            searchTerms: ['1/2'],
            value: MaterialThickness['1_1/2'],
          },
          {
            label: 'cutting:material_thickness_41.3',
            searchTerms: ['5/8'],
            value: MaterialThickness['1_5/8'],
          },
        ],
      },
    };

  export const CutterApplicationDefaultParameterSetTagEquipmentMapping: Record<
    string,
    ApplicationDefaultParameterSetTagData
  > = {
    [Cutter.HYPERTHERM_POWERMAX_SYNC]: HyperthermPowermaxSyncParameterSetTags,
    [Cutter.HYPERTHERM_POWERMAX_125]: HyperthermPowermax125ParameterSetTags,
    [Cutter.THERMAL_DYNAMICS_CUTMASTER]:
      ThermalDynamicsCutmasterParameterSetTags,
  };

  const RadialCutContiguousRule: FlowRule = {
    execute: (flow: Flow): RuleValidationResponse | undefined => {
      return flowRuleCheckContiguousByType({
        flow,
        itemTypeRef: FlowItemRef.CUT,
        parameterTypeRef: FlowItemParameterRef.CUT_TYPE,
        minRequired: 3,
        primaryTypes: [CutTypeEnum.RADIAL],
        followedByTypes: [CutTypeEnum.LINEAR, CutTypeEnum.RADIAL],
        followedByStartOffset: 2, // we need to contiguous radials and then the third point can be followed by valid weld type (non-tack)
        suffixMessage: getLocalizedText(
          'cutting:more_radial_points_require'
        ) as string,
      });
    },
  };

  const LinearCutContiguousRule: FlowRule = {
    execute: (flow: Flow): RuleValidationResponse | undefined => {
      return flowRuleCheckContiguousByType({
        flow,
        itemTypeRef: FlowItemRef.CUT,
        parameterTypeRef: FlowItemParameterRef.CUT_TYPE,
        minRequired: 2,
        primaryTypes: [CutTypeEnum.LINEAR],
        suffixMessage: getLocalizedText(
          'cutting:more_linear_point_required'
        ) as string,
        precededByTypes: [CutTypeEnum.RADIAL],
        followedByTypes: [CutTypeEnum.LINEAR, CutTypeEnum.RADIAL],
      });
    },
  };

  export const FlowValidationRules = [
    LinearCutContiguousRule,
    RadialCutContiguousRule,
  ];

  type ConfigKey = keyof FlowConfig;

  export const RECOMMENDATION_CONFIG_INPUTS: ConfigKey[] = [
    'gas',
    'current',
    'materialType',
    'materialThickness',
    'cutting',
  ];

  export const RECOMMENDED_PARAM_OUTPUTS: FlowItemParameterRef[] = [
    BeaconCuttingData.FlowItemParameterRef.TRAVEL_SPEED,
    BeaconCuttingData.FlowItemParameterRef.CUT_HEIGHT,
    BeaconCuttingData.FlowItemParameterRef.PIERCE_HEIGHT,
    BeaconCuttingData.FlowItemParameterRef.PIERCE_DELAY,
    BeaconCuttingData.FlowItemParameterRef.VOLTAGE,
    BeaconCuttingData.FlowItemParameterRef.KERF_WIDTH,
    BeaconCuttingData.FlowItemParameterRef.EDGE_START,
  ];
}
